import { safeLocalStorage } from '@toss/storage';

import {
  type GoogleClassroomPageContext,
  LandingPageType,
  UserType,
} from '../googleClassroom/types';

export const EXTERNAL_PLATFORM_DEBUG_STORAGE_KEY =
  'ZEP_QUIZ_EXTERNAL_PLATFORM_UI_DEBUG';

/**
 * Google Classroom 기능은 Google Classroom 사이트 내 iframe에서만 사용할 수 있다.
 * 다만 Google Classroom 사이트에서는 라이브(quiz.zep.us) 환경의 젭퀴즈만 접근할 수 있어
 * 개발 중 UI 분기 확인이 까다롭다.
 *
 * 이 디버그 값은 Google Classroom API나 과제 생성 같은 실제 기능을 활성화하지 않는다.
 * Google Classroom 안/밖, 교사/학생별 UI 분기를 로컬/개발 환경에서 확인하기 위한 용도다.
 */
export const LEGACY_GOOGLE_CLASSROOM_DEBUG_STORAGE_KEY =
  'IS_IN_GOOGLE_CLASSROOM_FOR_UI_DEBUG';

const DEBUG_LMS_QUERY_PARAM = '__debugLms';
const DEBUG_LMS_ROLE_QUERY_PARAM = '__debugLmsRole';

export type ExternalPlatformDebugMode = 'none' | 'lti' | 'googleClassroom';
export type ExternalPlatformDebugRole = 'student' | 'instructor';

export interface ExternalPlatformDebugOverride {
  mode: ExternalPlatformDebugMode;
  role: ExternalPlatformDebugRole;
}

function isDebugEnabled(): boolean {
  return process.env.NEXT_PUBLIC_STAGE !== 'PRD';
}

function normalizeMode(value: string | null): ExternalPlatformDebugMode | null {
  switch (value) {
    case 'none':
    case 'off':
    case 'false':
      return 'none';
    case 'lti':
      return 'lti';
    case 'gc':
    case 'google-classroom':
    case 'googleClassroom':
      return 'googleClassroom';
    case null:
    default:
      return null;
  }
}

function normalizeRole(value: string | null): ExternalPlatformDebugRole {
  return value === 'teacher' || value === 'instructor'
    ? 'instructor'
    : 'student';
}

function parseStoredOverride(
  value: string | null,
): ExternalPlatformDebugOverride | null {
  if (!value) return null;

  const directMode = normalizeMode(value);
  if (directMode) return { mode: directMode, role: 'student' };

  try {
    const parsed = JSON.parse(value) as {
      mode?: string;
      role?: string;
    };
    const mode = normalizeMode(parsed.mode ?? null);
    if (!mode) return null;
    return {
      mode,
      role: normalizeRole(parsed.role ?? null),
    };
  } catch {
    return null;
  }
}

function getSearchParams(): URLSearchParams | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search);
}

export function getExternalPlatformDebugOverride(): ExternalPlatformDebugOverride | null {
  if (!isDebugEnabled()) return null;

  const searchParams = getSearchParams();
  const queryMode = normalizeMode(
    searchParams?.get(DEBUG_LMS_QUERY_PARAM) ?? null,
  );
  if (queryMode) {
    return {
      mode: queryMode,
      role: normalizeRole(
        searchParams?.get(DEBUG_LMS_ROLE_QUERY_PARAM) ?? null,
      ),
    };
  }

  const storedOverride = parseStoredOverride(
    safeLocalStorage.get(EXTERNAL_PLATFORM_DEBUG_STORAGE_KEY),
  );
  if (storedOverride) return storedOverride;

  if (
    safeLocalStorage.get(LEGACY_GOOGLE_CLASSROOM_DEBUG_STORAGE_KEY) === 'true'
  ) {
    return { mode: 'googleClassroom', role: 'student' };
  }

  return null;
}

export function getGoogleClassroomDebugContext(): GoogleClassroomPageContext | null {
  const override = getExternalPlatformDebugOverride();
  if (override?.mode !== 'googleClassroom') return null;

  if (override.role === 'instructor') {
    return {
      pageType: LandingPageType.TeacherView,
      userType: UserType.Teacher,
      params: {
        courseId: 'debug-course',
        itemId: 'debug-item',
        itemType: 'courseWork',
        attachmentId: 'debug-attachment',
      },
    };
  }

  return {
    pageType: LandingPageType.StudentView,
    userType: UserType.Student,
    params: {
      courseId: 'debug-course',
      itemId: 'debug-item',
      itemType: 'courseWork',
      attachmentId: 'debug-attachment',
    },
  };
}
